import { useEffect, useState } from "react";
import { Check, CheckCheck, Flame, Clock, Camera, Reply } from "lucide-react";
import { timerLabel } from "@/lib/selectors";
import { Attachment } from "@/components/aph/Attachment";
import { SnapBubble } from "@/components/aph/SnapBubble";
import { isNative } from "@/lib/native";

const fmtTime = (ts) => new Date(ts).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
const URL_RE = /(https?:\/\/[^\s<]+|www\.[^\s<]+|(?:[a-z0-9-]+\.)+(?:app|com|net|org|io|ph|co|dev|me)(?:\/[^\s<]*)?)/gi;
const TRAIL = /[.,;:!?)\]'"]+$/;

function Linkified({ text, mine }) {
  const str = String(text || "");
  const parts = [];
  let last = 0;
  for (const m of str.matchAll(URL_RE)) {
    const prev = str[m.index - 1];
    if (prev && /[@\w./]/.test(prev)) continue;
    const raw = m[0].replace(TRAIL, "");
    if (!raw) continue;
    if (m.index > last) parts.push(<span key={`t${last}`}>{str.slice(last, m.index)}</span>);
    const href = /^https?:\/\//i.test(raw) ? raw : `https://${raw}`;
    parts.push(<a key={m.index} href={href} target="_blank" rel="noopener noreferrer" onClick={(e) => { if (isNative()) { e.preventDefault(); window.location.href = href; } }} data-testid="message-link" className={`underline underline-offset-2 break-all ${mine ? "text-stellar" : "text-stellar/90"} hover:opacity-80`}>{raw}</a>);
    last = m.index + raw.length;
  }
  if (last < str.length) parts.push(<span key={`t${last}`}>{str.slice(last)}</span>);
  return parts;
}

function useNow(active) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    if (!active) return;
    const t = setInterval(() => setNow(Date.now()), 250);
    return () => clearInterval(t);
  }, [active]);
  return now;
}

function Receipt({ m, peers }) {
  const read = m.read_by.length;
  const state = read ? "read" : m.delivered_to.length || m.status === "delivered" ? "delivered" : "sent";
  const Icon = state === "sent" ? Check : CheckCheck;
  return (
    <span data-testid={`message-receipt-${m.message_id}`} data-state={state} title={state} className={`flex items-center gap-0.5 ${state === "read" ? "text-stellar" : "text-ash/40"}`}>
      <Icon size={14} strokeWidth={state === "read" ? 2 : 1.5} />
      {peers > 1 && read > 0 && <span className="text-[10px] font-mono">{read}/{peers}</span>}
    </span>
  );
}

function BurnMeter({ m, now }) {
  const left = Math.max(0, m.burn_at - now);
  const pct = Math.min(100, (left / (m.timer * 1000)) * 100);
  return (
    <div className="mt-2" data-testid={`message-burn-${m.message_id}`}>
      <div className="flex items-center gap-1 text-[11px] text-flare font-mono"><Flame size={12} strokeWidth={1.5} /> {Math.ceil(left / 1000)}s</div>
      <div className="h-[2px] bg-flare/15 rounded mt-1 overflow-hidden"><div className="h-full bg-flare transition-[width] duration-200" style={{ width: `${pct}%` }} /></div>
    </div>
  );
}

export function MessageBubble({ m, mine, senderName, peers }) {
  const burning = m.status === "burning" && m.burn_at;
  const now = useNow(burning && !m.content.snap);

  if (m.content.snap) return <SnapBubble m={m} mine={mine} senderName={senderName} />;
  if (m.content.sys === "screenshot") {
    return (
      <div className="flex justify-center" data-testid={`message-${m.message_id}`} data-state="system">
        <span className="text-xs text-flare flex items-center gap-1.5 px-3 py-1.5 border border-flare/30 rounded-secure bg-nebula" data-testid={`screenshot-alert-${m.message_id}`}><Camera size={13} strokeWidth={1.5} /> {senderName || "They"} took a screenshot of your Snap</span>
      </div>
    );
  }

  if (m.status === "destroyed") {
    return (
      <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`message-${m.message_id}`} data-state="destroyed">
        <span className="burned text-xs italic text-flare/70 flex items-center gap-1.5 px-3 py-2 border border-dashed border-flare/25 rounded-secure"><Flame size={13} strokeWidth={1.5} /> Message burned</span>
      </div>
    );
  }

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"} rise-in`} data-testid={`message-${m.message_id}`} data-state={m.status}>
      <div className={`max-w-[78%] sm:max-w-[65%] p-3 rounded-secure backdrop-blur-md ${mine ? "bg-nebula border border-stellar/30 shadow-stellar-glow" : "bg-void border border-rim"}`}>
        {senderName && !mine && <p className="text-[11px] font-branding uppercase tracking-wider text-stellar/80 mb-1">{senderName}</p>}
        {m.content.story && (
          <p className="text-[11px] text-ash/50 border-l-2 border-stellar/50 pl-2 mb-1.5 flex items-center gap-1 truncate" data-testid={`story-quote-${m.message_id}`}>
            <Reply size={12} strokeWidth={1.5} /> {m.content.react ? (mine ? "Reacted to their story" : "Reacted to your story") : mine ? "Replied to their story" : "Replied to your story"}{m.content.story.text ? `: “${m.content.story.text}”` : " · photo"}
          </p>
        )}
        {m.content.react ? <p className="text-4xl leading-none py-1" data-testid={`story-reaction-${m.message_id}`}>{m.content.react}</p> : m.content.file ? <Attachment m={m} /> : <p className="text-sm leading-relaxed whitespace-pre-wrap break-words" data-testid={`message-text-${m.message_id}`}><Linkified text={m.content.text} mine={mine} /></p>}
        {burning && <BurnMeter m={m} now={now} />}
        <div className="flex items-center justify-end gap-2 mt-1.5 text-[10px] text-ash/40">
          {m.timer > 0 && !burning && <span className="flex items-center gap-1 text-flare/70"><Clock size={11} strokeWidth={1.5} /> {timerLabel(m.timer)}</span>}
          <span>{fmtTime(m.timestamp)}</span>
          {mine && <Receipt m={m} peers={peers} />}
        </div>
      </div>
    </div>
  );
}
